import { useGetSigningUrlMutation } from '@/services/contractSigning';
import { PalmettoButton } from '@palmetto/dsm-components';
import { Spinner } from '@palmetto/palmetto-components';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Locale } from 'types';

import { Copy, copyText, inlineCopy, SupportContact } from './ContractSigningCopy';

const POLL_INTERVAL_MS = 3000;
// Stops a stuck submission from polling forever; the unavailable state offers a retry and support contact.
const POLL_TIMEOUT_MS = 60 * 60 * 1000;

type Phase = 'idle' | 'waiting' | 'unavailable';

/** Spanish signers only: Conga's own screens may still be in English, so they get the words they'll need. */
const Coaching = ({ locales }: { locales: Locale[] }) => (
  <div className="gap-3 flex flex-col rounded-16 bg-canvas-bolder p-4">
    <Copy copyKey="coachingHeading" locales={locales} />
    <Copy copyKey="coachingBody" locales={locales} />
    <ul className="body-300 text-text-primary">
      <li>
        <strong>Accept</strong> = {copyText(Locale.es, 'coachingAccept')}
      </li>
      <li>
        <strong>Sign</strong> = {copyText(Locale.es, 'coachingSign')}
      </li>
      <li>
        <strong>Finish</strong> = {copyText(Locale.es, 'coachingFinish')}
      </li>
    </ul>
  </div>
);

/**
 * "Review and sign": the first click may arrive before Conga has issued signing URLs, so the page polls
 * until they're ready and then leaves for Conga.
 */
export const ReviewAndSignStep = ({
  inviteToken,
  locales,
  contractLanguage,
}: {
  inviteToken: string;
  locales: Locale[];
  contractLanguage: Locale;
}) => {
  const [getSigningUrl, { isLoading }] = useGetSigningUrlMutation();
  const [phase, setPhase] = useState<Phase>('idle');
  // A repeated 'waiting' leaves phase unchanged, so this is what re-arms the poll timer.
  const [waitCount, setWaitCount] = useState(0);
  const pollStartedAt = useRef<number | null>(null);

  const requestSigningUrl = useCallback(async () => {
    try {
      const result = await getSigningUrl({ inviteToken }).unwrap();
      if (result.status === 'ready') {
        pollStartedAt.current = null;
        window.location.assign(result.url);
        return;
      }
      if (result.status === 'waiting') {
        pollStartedAt.current ??= Date.now();
        if (Date.now() - pollStartedAt.current > POLL_TIMEOUT_MS) {
          pollStartedAt.current = null;
          setPhase('unavailable');
          return;
        }
        setPhase('waiting');
        setWaitCount((count) => count + 1);
        return;
      }
      pollStartedAt.current = null;
      setPhase('unavailable');
    } catch {
      pollStartedAt.current = null;
      setPhase('unavailable');
    }
  }, [getSigningUrl, inviteToken]);

  useEffect(() => {
    if (phase !== 'waiting') return;
    const timer = setTimeout(() => void requestSigningUrl(), POLL_INTERVAL_MS);
    return () => clearTimeout(timer);
  }, [phase, waitCount, requestSigningUrl]);

  const handleClick = useCallback(() => {
    void requestSigningUrl();
  }, [requestSigningUrl]);

  if (phase === 'waiting') {
    return (
      <div className="flex flex-col items-center gap-4 text-center">
        <Spinner />
        <Copy copyKey="waitingHeading" locales={locales} variant="heading" />
        <Copy copyKey="waitingBody" locales={locales} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <Copy copyKey="reviewHeading" locales={locales} variant="heading" />
      {contractLanguage === Locale.es && <Coaching locales={locales} />}
      <PalmettoButton type="button" emphasis="primary" size="large" loading={isLoading} onClick={handleClick}>
        {inlineCopy(locales, 'reviewAndSign')}
      </PalmettoButton>
      {phase === 'unavailable' && (
        <>
          <Copy copyKey="unavailableBody" locales={locales} variant="error" />
          <SupportContact locales={locales} />
        </>
      )}
    </div>
  );
};
